import { useViewFeaturesStore, TerrainFeatureType } from '@/store/viewFeaturesStore';
import { WeaveButton } from '../UI/WeaveButton/WeaveButton';
import { clearAllLandmarks, clearAllPaintedAreas } from '@/utils/viewFeatures';
import * as THREE from 'three';
import styles from './styles.module.scss';
import { useThreeSceneStore } from '@/store/threeSceneStore';

interface ViewFeaturesPanelProps {
	sceneRef: React.MutableRefObject<THREE.Scene | null>;
}

export const ViewFeaturesPanel = ({ sceneRef }: ViewFeaturesPanelProps) => {
	const {
		isLandmarkSelectionMode,
		setIsLandmarkSelectionMode,
		isPaintMode,
		setIsPaintMode,
		isPaintDeletionMode,
		setIsPaintDeletionMode,
		currentPaintType,
		setCurrentPaintType,
		landmarks,
		paintedAreas,
		clearPaintPoints,
	} = useViewFeaturesStore();

	const { resetAllModes } = useThreeSceneStore();

	const handleLandmarkModeToggle = () => {
		const enabled = !isLandmarkSelectionMode;

		if (enabled && isPaintMode) {
			setIsPaintMode(false);
			setIsPaintDeletionMode(false);
			clearPaintPoints();
		}

		setIsLandmarkSelectionMode(enabled);
		resetAllModes();
	};

	const handlePaintModeToggle = () => {
		const enabled = !isPaintMode;

		if (enabled && isLandmarkSelectionMode) {
			setIsLandmarkSelectionMode(false);
		}
		if (enabled && isPaintDeletionMode) {
			setIsPaintDeletionMode(false);
		}

		setIsPaintMode(enabled);
		if (!enabled) {
			clearPaintPoints();
		}
		resetAllModes();
	};

	const handlePaintDeletionModeToggle = () => {
		const enabled = !isPaintDeletionMode;

		if (enabled && isPaintMode) {
			setIsPaintMode(false);
			clearPaintPoints();
		}
		if (enabled && isLandmarkSelectionMode) {
			setIsLandmarkSelectionMode(false);
		}

		setIsPaintDeletionMode(enabled);
		resetAllModes();
	};

	const handlePaintTypeChange = (type: TerrainFeatureType) => {
		setCurrentPaintType(type);
	};

	const handleClearLandmarks = () => {
		if (confirm('Remove all landmarks? This will reset their appearance.')) {
			if (sceneRef.current) {
				clearAllLandmarks(sceneRef.current);
			}
		}
	};

	const handleClearPaintedAreas = () => {
		if (confirm('Remove all painted terrain features?')) {
			if (sceneRef.current) {
				clearAllPaintedAreas(sceneRef.current);
			}
		}
	};

	return (
		<div className={styles.panel}>
			<div>
				Windows with views of landmarks and natural features receive higher scores.
			</div>
			<div className={styles.section}>
				<p>
					Click buildings to mark as landmarks (key viewpoints)
				</p>
				<div className={styles.statusRow}>
					<WeaveButton
						onClick={handleLandmarkModeToggle}
						variant={isLandmarkSelectionMode ? 'solid' : 'outlined'}
					>
						Add/Remove Landmarks
					</WeaveButton>
					{landmarks.size > 0 && (
						<div className={styles.statusRow}>
							<span>
								{landmarks.size} landmark{landmarks.size !== 1 ? 's' : ''} marked
							</span>
							<WeaveButton
								variant="outlined"
								density="high"
								onClick={handleClearLandmarks}
							>
								Clear All
							</WeaveButton>
						</div>
					)}
				</div>
			</div>

			<div className={styles.section}>
				<p>
					Click and drag on terrain to paint natural features
				</p>
				<div className={styles.statusRow}>
					<div className={styles.buttonGroup}>
						<WeaveButton
							onClick={handlePaintModeToggle}
							variant={isPaintMode ? 'solid' : 'outlined'}
						>
							Paint Terrain Features
						</WeaveButton>
						<WeaveButton
							onClick={handlePaintDeletionModeToggle}
							variant={isPaintDeletionMode ? 'solid' : 'outlined'}
						>
							Remove Painted Areas
						</WeaveButton>
					</div>
					{paintedAreas.length > 0 && (
						<div className={styles.statusRow}>
							<span>
								{paintedAreas.length} area{paintedAreas.length !== 1 ? 's' : ''} painted
							</span>
							<WeaveButton
								variant="outlined"
								density="high"
								onClick={handleClearPaintedAreas}
							>
								Clear All
							</WeaveButton>
						</div>
					)}
				</div>
				{isPaintMode && (
					<div className={styles.buttonGroup}>
						<WeaveButton
							variant={currentPaintType === 'greenery' ? 'solid' : 'outlined'}
							density="high"
							onClick={() => handlePaintTypeChange('greenery')}
						>
							🌳 Greenery
						</WeaveButton>
						<WeaveButton
							variant={currentPaintType === 'water' ? 'solid' : 'outlined'}
							density="high"
							onClick={() => handlePaintTypeChange('water')}
						>
							💧 Water
						</WeaveButton>
						<WeaveButton
							variant={currentPaintType === 'park' ? 'solid' : 'outlined'}
							density="high"
							onClick={() => handlePaintTypeChange('park')}
						>
							🏞️ Park
						</WeaveButton>
					</div>
				)}
			</div>
		</div>
	);
};
